import { useMutation, useQuery } from '@apollo/client';
import { LocationAddressFormValues } from 'components/pages/user/account/locations/locations-form';
import { GEOADDRESS_FIELDS } from 'components/ui/fields/address';
import { useToast } from 'components/ui/toast';
import { format } from 'date-fns';
import gql from 'graphql-tag';
import {
	LocationAddressUpdate,
	Mutation,
	MutationOrganizationAccountLocationAddressAddOrUpdateArgs,
	MutationOrganizationAccountLocationAddressDeleteArgs,
	Query,
	QueryOrganizationLocationAddressesArgs,
	SortDirection,
} from 'middleware-types';
import { handleNoResponse, responseHasErrors } from 'utils/errors';
import { zoneDateOnly } from 'utils/utils';

/**
 * Delete an organization location address.
 */
export const DELETE_ORG_LOCATION_ADDRESS = gql`
	mutation organizationAccountLocationAddressDelete(
		$organizationId: ID!
		$locationAddressId: ID!
	) {
		organizationAccountLocationAddressDelete(
			organizationId: $organizationId
			locationAddressId: $locationAddressId
		)
	}
`;

/**
 * Hook to delete an organization location address.
 * @param organizationId
 * @param options
 * @returns
 */
export const useDeleteOrgLocationAddressMutation = (organizationId: string) => {
	const toast = useToast();
	const [orgAccountLocationAddressDelete, { loading }] = useMutation<
		Pick<Mutation, 'organizationAccountLocationAddressDelete'>,
		MutationOrganizationAccountLocationAddressDeleteArgs
	>(DELETE_ORG_LOCATION_ADDRESS, {
		update: (cache) => {
			cache.evict({
				id: 'ROOT_QUERY',
				fieldName: 'organizationLocationAddresses',
			});

			cache.gc();
		},
	});

	return {
		deleteLocation: async (locationAddressId: string) => {
			return orgAccountLocationAddressDelete({
				variables: {
					organizationId,
					locationAddressId,
				},
			})
				.then((res) => {
					if (responseHasErrors(res.errors, { toast })) {
						return false;
					}
					toast.push('Location address deleted successfully.', {
						variant: 'success',
					});
					return true;
				})
				.catch(() => {
					handleNoResponse({ toast });
					return false;
				});
		},
		loading,
	};
};

/**
 * Add or update a organization location address.
 */
export const ADD_OR_UPDATE_ORG_LOCATION_ADDRESS = gql`
	${GEOADDRESS_FIELDS}
	mutation organizationAccountLocationAddressAddOrUpdate(
		$organizationId: ID!
		$locationAddressId: ID
		$update: LocationAddressUpdate!
	) {
		organizationAccountLocationAddressAddOrUpdate(
			organizationId: $organizationId
			locationAddressId: $locationAddressId
			update: $update
		) {
			id
			parentId
			addressType
			fromDate
			toDate
			address {
				...GeoAddressFields
			}
		}
	}
`;

/**
 * Hook to add or update an organization location address.
 * @param organizationId
 * @returns
 */
export const useAddOrUpdateOrgLocationAddressMutation = (organizationId: string) => {
	const toast = useToast();
	const [locationAddOrUpdate, { loading, error }] = useMutation<
		Pick<Mutation, 'organizationAccountLocationAddressAddOrUpdate'>,
		MutationOrganizationAccountLocationAddressAddOrUpdateArgs
	>(ADD_OR_UPDATE_ORG_LOCATION_ADDRESS, {
		update: (cache) => {
			cache.evict({
				id: 'ROOT_QUERY',
				fieldName: 'organizationLocationAddresses',
			});

			cache.gc();
		},
	});
	const addOrUpdate = async (location: LocationAddressFormValues, locationId?: string) => {
		return locationAddOrUpdate({
			variables: {
				organizationId,
				locationAddressId: locationId,
				update: {
					...(location as LocationAddressUpdate),
					fromDate: location.fromDate ? format(location.fromDate, 'yyyy-MM-dd') : null,
					toDate: location.toDate ? format(location.toDate, 'yyyy-MM-dd') : null,
				},
			},
		})
			.then(async (res) => {
				if (responseHasErrors(res.errors, { toast })) {
					return false;
				}
				toast.push('Location information updated successfully.', {
					variant: 'success',
				});
				return true;
			})
			.catch(() => {
				handleNoResponse({ toast });
				return false;
			});
	};

	return { addOrUpdate, loading, error };
};

/**
 * Organization locations query.
 */
export const ORG_LOCATIONS = gql`
	${GEOADDRESS_FIELDS}
	query organizationLocationAddresses(
		$organizationId: ID!
		$pageSize: Float!
		$offset: Float!
		$sortDirection: SortDirection!
		$searchFor: String!
	) {
		organizationLocationAddresses(
			organizationId: $organizationId
			pageSize: $pageSize
			offset: $offset
			sortDirection: $sortDirection
			searchFor: $searchFor
		) {
			totalCount
			items {
				id
				parentId
				addressType
				fromDate
				toDate
				address {
					...GeoAddressFields
				}
			}
		}
	}
`;

/**
 * Hook to query the organization locations.
 * @param organizationId
 * @returns
 */
export const useOrgLocationsQuery = (organizationId: string, pageSize: number, page: number) => {
	const { data, loading, error } = useQuery<
		Pick<Query, 'organizationLocationAddresses'>,
		QueryOrganizationLocationAddressesArgs
	>(ORG_LOCATIONS, {
		variables: {
			organizationId,
			pageSize,
			offset: (page - 1) * pageSize,
			sortDirection: SortDirection.Ascending,
			searchFor: '',
		},
	});

	return {
		locations:
			data?.organizationLocationAddresses?.items?.map((la) => ({
				id: la.id,
				address: la.address,
				addressType: la.addressType,
				fromDate: la?.fromDate ? zoneDateOnly(la?.fromDate) : null,
				toDate: la?.toDate ? zoneDateOnly(la.toDate) : null,
			})) ?? [],
		totalCount: data?.organizationLocationAddresses.totalCount ?? 0,
		loading,
		error,
	};
};
